import { useState, useEffect, useCallback } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { signOut } from 'next-auth/react';
import { workspacesApi, Workspace } from '@/lib/api';
import { fetchWorkspaceTags } from '@/lib/workspaceAvatarTags';
import {
    normalizeWorkspace,
    unwrapWorkspaceResponse,
} from '@/lib/workspaceAvatar';
import { preloadWorkspaceAvatars } from '@/lib/workspaceImageCache';
import { useAuthState } from "@/hooks/useAuthState";
import { clearClientAuthSession } from "@/lib/clearClientAuthSession";
import { assignInPlaceAppLocationIfLineWebView } from "@/lib/openLineExternalBrowser";

const DEFAULT_AVATAR = 'https://ui-avatars.com/api/?name=User&background=random';

function readStoredGoogleProfile(): {
  displayName?: string;
  email?: string;
  pictureUrl?: string;
} | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem('google_profile');
    if (!raw?.trim()) return null;
    const p = JSON.parse(raw) as Record<string, unknown>;
    return {
      displayName: typeof p.displayName === 'string' ? p.displayName : undefined,
      email: typeof p.email === 'string' ? p.email : undefined,
      pictureUrl: typeof p.pictureUrl === 'string' ? p.pictureUrl : undefined,
    };
  } catch {
    return null;
  }
}

/** Match `/signin` + Google callback shapes: backend user may omit nested `profile`. */
function resolveAvatarFromUserRecord(user: Record<string, unknown> | null, gp: ReturnType<typeof readStoredGoogleProfile>): string | null {
  const profile = (user?.profile as Record<string, unknown> | undefined) || {};
  const fromUser =
    (typeof user?.avatar === 'string' && user.avatar.trim()) ||
    (typeof user?.picture === 'string' && user.picture.trim()) ||
    (typeof user?.image === 'string' && user.image.trim()) ||
    (typeof user?.photo === 'string' && user.photo.trim()) ||
    (typeof profile.avatar === 'string' && profile.avatar.trim()) ||
    (typeof profile.pictureUrl === 'string' && profile.pictureUrl.trim()) ||
    (typeof profile.picture === 'string' && profile.picture.trim()) ||
    null;
  const fromGp = gp?.pictureUrl?.trim() || null;
  return fromUser || fromGp;
}

function resolveDisplayNameFromUserRecord(user: Record<string, unknown> | null, gp: ReturnType<typeof readStoredGoogleProfile>): string | null {
  const profile = (user?.profile as Record<string, unknown> | undefined) || {};
  const pFn = profile.firstName;
  const pLn = profile.lastName;
  const uFn = user?.firstName;
  const uLn = user?.lastName;
  const firstLast =
    [
      typeof pFn === 'string' ? pFn : '',
      typeof pLn === 'string' ? pLn : '',
    ]
      .filter(Boolean)
      .join(' ')
      .trim() ||
    [
      typeof uFn === 'string' ? uFn : '',
      typeof uLn === 'string' ? uLn : '',
    ]
      .filter(Boolean)
      .join(' ')
      .trim();
  const fromUser =
    (typeof profile.displayName === 'string' && profile.displayName.trim()) ||
    (typeof user?.displayName === 'string' && user.displayName.trim()) ||
    (typeof user?.name === 'string' && user.name.trim()) ||
    firstLast ||
    (typeof user?.email === 'string' && user.email.trim()) ||
    (typeof profile.email === 'string' && profile.email.trim()) ||
    null;
  const fromGp =
    gp?.displayName?.trim() ||
    gp?.email?.trim() ||
    null;
  return fromUser || fromGp || null;
}

export function useWorkerLayout() {
    const { isLoggedIn, isLoadingSession, sessionStatus } = useAuthState();
    const router = useRouter();
    const pathname = usePathname();
    
    // Note: currentWorkspaceId and isEmbedded should be obtained at component level
    // using useSearchParams wrapped in Suspense, not in this hook

    const [userId, setUserId] = useState<string | null>(null);
    const [userAvatar, setUserAvatar] = useState<string>(DEFAULT_AVATAR);
    const [userDisplayName, setUserDisplayName] = useState<string | null>(null);
    const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
    const [isWorkspaceModalOpen, setIsWorkspaceModalOpen] = useState<boolean>(false);
    const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
    const [isLoadingWorkspaces, setIsLoadingWorkspaces] = useState<boolean>(true);

    // ... existing fetchWorkspaces ...
    const fetchWorkspaces = async () => {
        try {
            setIsLoadingWorkspaces(true);

            const data = await workspacesApi.list() as any;


            // Check for various response structures
            let list: Workspace[] = [];
            if (Array.isArray(data)) {
                list = data;
            } else if (data && Array.isArray(data.data)) {
                list = data.data;
            } else if (data && Array.isArray(data.workspaces)) {
                list = data.workspaces;
            }


            const listWithTags = await Promise.all(
                list.map(async (ws) => {
                    if (!ws?.id) {
                        return normalizeWorkspace(ws as unknown as Record<string, unknown>);
                    }

                    const tags = await fetchWorkspaceTags(ws.id);
                    let merged = normalizeWorkspace({
                        ...(ws as unknown as Record<string, unknown>),
                        tags,
                    });

                    if (!merged.imageUrl?.trim()) {
                        try {
                            const detail = unwrapWorkspaceResponse<Record<string, unknown>>(
                                await workspacesApi.getById(ws.id),
                            );
                            if (detail) {
                                merged = normalizeWorkspace({
                                    ...merged,
                                    ...detail,
                                    tags,
                                });
                            }
                        } catch {
                            // Best-effort: list payload may omit imageUrl.
                        }
                    }

                    return merged;
                }),
            );

            void preloadWorkspaceAvatars(listWithTags as Workspace[]);
            setWorkspaces(listWithTags as Workspace[]);
        } catch (error) {
            console.error('Failed to fetch workspaces', error);
        } finally {
            setIsLoadingWorkspaces(false);
        }
    };

    useEffect(() => {
        if (isLoggedIn) {
            fetchWorkspaces();
        }
    }, [isLoggedIn]);

    const syncUserData = useCallback(() => {
        if (!isLoggedIn) {
            setUserId(null);
            setUserAvatar(DEFAULT_AVATAR);
            setUserDisplayName(null);
            setWorkspaces([]);
            return;
        }

        let user: Record<string, unknown> | null = null;
        const userData = localStorage.getItem('user');
        if (userData) {
            try {
                user = JSON.parse(userData) as Record<string, unknown>;
            } catch (parseError) {
                console.error('Error parsing user data:', parseError);
            }
        }

        const gp = readStoredGoogleProfile();

        if (user?.id) {
            setUserId(String(user.id));
        } else {
            setUserId(null);
        }

        const avatar = resolveAvatarFromUserRecord(user, gp);
        setUserAvatar(avatar || DEFAULT_AVATAR);

        const displayName = resolveDisplayNameFromUserRecord(user, gp);
        setUserDisplayName(displayName);
    }, [isLoggedIn]);

    useEffect(() => {
        syncUserData();
        const onAuth = () => syncUserData();
        window.addEventListener('auth:login', onAuth);
        window.addEventListener('auth:loginSuccess', onAuth);
        return () => {
            window.removeEventListener('auth:login', onAuth);
            window.removeEventListener('auth:loginSuccess', onAuth);
        };
    }, [syncUserData, pathname]);

    const handleLogoutAction = async () => {
        // Clear localStorage, `/signin` session flags, and httpOnly auth cookie (see clearClientAuthSession).
        await clearClientAuthSession();
        setUserId(null);
        setUserAvatar(DEFAULT_AVATAR);
        setWorkspaces([]);
        window.dispatchEvent(new Event('auth:logout'));
        setIsDropdownOpen(false);

        try {
            await signOut({ redirect: false });
        } catch (error) {
            console.error('Sign out failed, local logout already applied:', error);
        } finally {
            // LINE in-app: full reload on app origin (not liff.line.me — that often opens a new WebView window).
            if (assignInPlaceAppLocationIfLineWebView("/")) {
                return;
            }
            router.push("/");
        }
    };

    return {
        router,
        pathname,
        userAvatar,
        userDisplayName,
        isLoggedIn,
        isDropdownOpen,
        setIsDropdownOpen,
        isWorkspaceModalOpen,
        setIsWorkspaceModalOpen,
        workspaces,
        isLoadingWorkspaces,
        isLoadingSession,
        fetchWorkspaces,
        handleLogoutAction,
        userId,
        sessionStatus,
    };
}
